// Vendor Libraries
import React, { Children, Component, isValidElement } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { get, map, noop, includes, isEmpty } from 'lodash';

// Components
import AccordionButton from '../core/accordion-button';
import AccordionPanel from '../core/accordion-panel';
import Button from '../core/button';
import Form from '../core/form';
import FormButtons from '../core/form-buttons';

// Redux Actions
import {
  searchFormAddField,
  searchFormAddFields,
  searchFormRemoveField,
  searchFormResetFields,
  unregisterFieldValue,
} from '../../actions/form-data-actions';

// Contexts
import { SearchContext } from '../../contexts/search-context';

// Styles
import './services-search-form.scss';

class ServicesSearchForm extends Component {
  form = null;

  selectInput = null;

  state = {
    advancedSearchExpanded: false,
  };

  componentWillMount() {
    const { advancedSearch, searchFormResetFields } = this.props;
    const { name } = this.getNameServicesDataKey();

    if (advancedSearch && isEmpty(this.getVisibleFields())) {
      searchFormResetFields(name);
    }
  }

  componentDidMount() {
    const { noFocus, noScroll } = this.props;

    if (this.form && !noScroll) {
      this.form.scrollIntoView(false);
    }

    if (this.form && !noFocus) {
      const input = this.form.querySelector('input[type=text]');

      if (input && input.focus && input.setSelectionRange) {
        input.focus();
        input.setSelectionRange(0, 1000);
      }
    }
  }

  getClass() {
    const { advancedSearch, className } = this.props;

    return classnames(
      {
        'services-search-form': true,
        'services-search-form_advanced': advancedSearch,
      },
      className
    );
  }

  getNameServicesDataKey(props = this.props) {
    const { servicesDataKey } = this.context;
    const { formName, servicesDataKey: servicesDataKeyProp } = props;
    const currentServicesDataKey = servicesDataKeyProp || servicesDataKey;

    return {
      name: formName || currentServicesDataKey,
      servicesDataKey: currentServicesDataKey,
    };
  }

  getProps() {
    const { name, servicesDataKey } = this.getNameServicesDataKey();

    return {
      className: this.getClass(),
      formRef: (form) => (this.form = form),
      name,
      onReset: this.handleReset,
      onSubmit: this.handleSubmit,
      servicesDataKey,
    };
  }

  getSelectProps() {
    return {
      className: 'services-search-form--select',
      ref: (selectInput) => (this.selectInput = selectInput),
    };
  }

  getVisibleFields() {
    const { formData } = this.props;
    const { name } = this.getNameServicesDataKey();

    return get(formData, [name, 'visibleFields'], []);
  }

  handleAddClick = (event) => {
    event && event.preventDefault && event.preventDefault();

    const { searchFormAddField, searchFormAddFields } = this.props;
    const { name } = this.getNameServicesDataKey();
    const fieldName = get(this.selectInput, 'value');

    if (fieldName === 'all') {
      searchFormAddFields(name, Object.keys(this.findFormInputs()));
    } else if (fieldName) {
      searchFormAddField(name, fieldName);
    }
  };

  handleHideClick = (event, fieldName) => {
    event && event.preventDefault && event.preventDefault();

    const { searchFormRemoveField, unregisterFieldValue } = this.props;
    const { name } = this.getNameServicesDataKey();

    searchFormRemoveField(name, fieldName, false);
    unregisterFieldValue(name, fieldName);
  };

  handleReset = () => {
    const { resetSort, refreshServicesData } = this.context;

    resetSort().then(() => {
      const { onReset = noop, noRefreshOnReset } = this.props;

      if (!noRefreshOnReset) {
        refreshServicesData();
      }

      onReset();
    });
  };

  handleResetClick = (event) => {
    const { resetForm } = this.context;
    const { searchFormResetFields } = this.props;

    event && event.preventDefault && event.preventDefault();

    const { name } = this.getNameServicesDataKey();

    searchFormResetFields(name);
    resetForm(false); // withSearch
  };

  handleSubmit = () => {
    const { onSubmit = noop } = this.props;
    const { servicesDataSearch } = this.context;

    servicesDataSearch();
    onSubmit();
  };

  findFormInputs(children) {
    const { children: childrenProp } = this.props;
    const formInputs = {};

    Children.map(children || childrenProp, (element) => {
      if (
        isValidElement(element)
                && element.props.name
                && element.props.label
                && element.props.visible === undefined
      ) {
        formInputs[element.props.name] = element;
      }
    });

    return formInputs;
  }

  renderAdvancedSearchForm() {
    const { children } = this.props;
    const formInputs = this.findFormInputs();
    const accordionProps = {
      className: 'services-search-form--accordion-panel',
      detailsRenderer: () => this.renderSearchFilters(formInputs),
      disableDefaultToggle: true,
      onChange: (advancedSearchExpanded) => this.setState({ advancedSearchExpanded }),
    };

    return (
            <>
                <div className="services-search-form--fields">
                    {Children.map(children, (element) => !isValidElement(element)
                        || element.props.visible !== undefined
                        || element.props.name === undefined
                        ? element
                        : null,
                    )}
                    {map(formInputs, (element) => this.renderVisibleField(element))}
                </div>
                <FormButtons noLabel={true} />
                <AccordionPanel {...accordionProps}>
                    <AccordionButton className="error--accordion-button">Advanced Search</AccordionButton>
                </AccordionPanel>
            </>
    );
  }

  renderSearchFilters = (formInputs) => {
    const visibleFields = this.getVisibleFields();

    return (
            <div className="services-search-form--accordion-details">
                <div className="services-search-form--select-control">
                    <select {...this.getSelectProps()}>
                        {map(formInputs, (element, name) => (!includes(visibleFields, name) ? (
                                <option key={name} value={name}>
                                    {element.props.label}
                                </option>
                            ) : null)
                        )}
                        <option value="all">Add All Fields</option>
                    </select>
                </div>
                <Button className="services-search-form--button" onClick={this.handleAddClick}>
                    add filter
                </Button>
                <Button className="services-search-form--button" onClick={this.handleResetClick}>
                    reset filters
                </Button>
            </div>
    );
  };

  renderVisibleField(element) {
    const { advancedSearchExpanded } = this.state;
    const hideButtonProps = {
      className: 'services-search-form--hide-field',
      onClick: (event) => this.handleHideClick(event, element.props.name),
    };

    return element ? (
            <div className="services-search-form--visible-field" key={element.props.name}>
                {advancedSearchExpanded ? <span {...hideButtonProps}>hide</span> : null}
                {element}
            </div>
    ) : null;
  }

  render() {
    const { advancedSearch, children } = this.props;
    const visibleFields = this.getVisibleFields();

    return (
            <Form {...this.getProps()}>
                {advancedSearch && children && !isEmpty(visibleFields) ? this.renderAdvancedSearchForm() : children}
            </Form>
    );
  }
}

ServicesSearchForm.propTypes = {
  advancedSearch: PropTypes.bool,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  formData: PropTypes.object.isRequired,
  formName: PropTypes.string,
  noFocus: PropTypes.bool,
  noRefreshOnReset: PropTypes.bool,
  noScroll: PropTypes.bool,
  onReset: PropTypes.func,
  onSubmit: PropTypes.func,
  searchFormAddField: PropTypes.func.isRequired,
  searchFormAddFields: PropTypes.func.isRequired,
  searchFormRemoveField: PropTypes.func.isRequired,
  searchFormResetFields: PropTypes.func.isRequired,
  servicesDataKey: PropTypes.string,
  unregisterFieldValue: PropTypes.func.isRequired,
};

ServicesSearchForm.contextType = SearchContext;

const mapStateToProps = (state) => ({
  formData: get(state, 'formData', {}),
});

const mapDispatchToProps = {
  searchFormAddField,
  searchFormAddFields,
  searchFormRemoveField,
  searchFormResetFields,
  unregisterFieldValue,
};

export default connect(mapStateToProps, mapDispatchToProps)(ServicesSearchForm);
